import { getDatabase, ref, set, get, push, remove, onValue } from "firebase/database";
import store from '@/store';
import { getAuth } from 'firebase/auth';

export async function deleteUser(workshopId, userId, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    const baseGlobal = `customers/${customerId}/workshop/${workshopId}`;
    const baseScoped = departmentId && projectId
        ? `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}`
        : null;

    // Delete under users/{id}
    await set(ref(db, `${baseGlobal}/users/${userId}`), null);
    if (baseScoped) {
        await set(ref(db, `${baseScoped}/users/${userId}`), null);
    }
    // Delete under participants/{id}
    await set(ref(db, `${baseGlobal}/participants/${userId}`), null);
    if (baseScoped) {
        await set(ref(db, `${baseScoped}/participants/${userId}`), null);
    }
    // Fallback: remove any participant entries that match by name (if values are objects with name)
    try {
        const snap = await get(ref(db, `${baseGlobal}/participants`));
        const participants = snap.val() || {};
        const deletions = [];
        for (const [pid, val] of Object.entries(participants)) {
            if (val && typeof val === 'object' && val.name && String(val.name).toLowerCase() === String(userId).toLowerCase()) {
                deletions.push(set(ref(db, `${baseGlobal}/participants/${pid}`), null));
                if (baseScoped) {
                    deletions.push(set(ref(db, `${baseScoped}/participants/${pid}`), null));
                }
            }
        }
        if (deletions.length) {
            await Promise.all(deletions);
        }
    } catch (e) {
        // no-op; fallback best-effort
    }
}

export async function onUsersChange(workshopId, callbackFunction, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    // Listen to participants (source of truth for attendees)
    let participantsRef;
    if (departmentId && projectId) {
        participantsRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/participants`);
    } else {
        participantsRef = ref(db, `customers/${customerId}/workshop/${workshopId}/participants`);
    }

    onValue(participantsRef, (snapshot) => {
        const participantsObject = snapshot.val();
        if (participantsObject == null) {
            callbackFunction([]);
        } else {
            const users = Object.keys(participantsObject).map(key => {
                const val = participantsObject[key];
                return {
                    id: key,
                    name: (val && typeof val === 'object' && val.name) ? val.name : key
                };
            });
            callbackFunction(users);
        }
    });
}

export async function loadWorkshop(customerId, workshopId) {
    if (!customerId || !workshopId) {
        console.error('loadWorkshop: Missing required parameters', { customerId, workshopId });
        return null;
    }

    try {
        console.log('Loading workshop:', { customerId, workshopId });
        const db = getDatabase();
        // Always use the global path for reading workshop data
        const workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
        const snapshot = await get(workshopRef);
        const data = snapshot.val();
        
        if (!data) {
            console.log('No workshop data found, creating initial structure');
            const initialData = {
                id: workshopId,
                customerId,
                startDate: null,
                results: {},
                selectedTopics: [],
                tasks: [],
                riskAssessment: {},
                qualityMetrics: {
                    testCoverage: 0,
                    codeQuality: 0,
                    performance: 0,
                    security: 0
                }
            };
            await set(workshopRef, initialData);
            return initialData;
        }
        // Ensure required properties exist
        data.results = data.results || {};
        data.selectedTopics = data.selectedTopics || [];
        data.tasks = data.tasks || [];
        data.riskAssessment = data.riskAssessment || {};
        data.qualityMetrics = data.qualityMetrics || {
            testCoverage: 0,
            codeQuality: 0,
            performance: 0,
            security: 0
        };
        data.customerId = data.customerId || customerId;
        return data;
    } catch (error) {
        console.error('Error loading workshop:', error);
        throw error;
    }
}

export async function createWorkshop(customerId, departmentId, projectId, companyName, name) {
    if (!customerId || !name) {
        console.error('createWorkshop: Missing required parameters', { customerId, name });
        return null;
    }

    try {
        const db = getDatabase();
        
        // Generate workshop ID
        const workshopId = Math.random().toString(16).slice(2);
        
        // Create initial workshop data
        const workshopData = {
            id: workshopId,
            departmentId,
            projectId,
            startDate: null,
            name: name,
            companyName: companyName,
            users: {},
            activeVotingOnSegmentId: "",
            endVotingTime: null,
            results: {},
            selectedTopics: [],
            tasks: [],
            riskAssessment: {},
            qualityMetrics: {
                testCoverage: 0,
                codeQuality: 0,
                performance: 0,
                security: 0
            }
        };
        
        // Create workshop in the workshops collection
        const workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
        await set(workshopRef, workshopData);

        // Add workshop reference to the project's workshops if project exists
        if (departmentId && projectId) {
            const projectWorkshopRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}`);
            // Write the full workshop data to the department/project path
            await set(projectWorkshopRef, workshopData);
        }

        console.log('Workshop created successfully:', workshopData);
        return workshopId;
    } catch (error) {
        console.error('Error creating workshop:', error);
        throw error;
    }
}

export async function startWorkshop(workshopId, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    const auth = getAuth();
    const uid = auth.currentUser ? auth.currentUser.uid : null;
    let basePath;
    if (departmentId && projectId) {
        basePath = `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}`;
    } else {
        basePath = `customers/${customerId}/workshop/${workshopId}`;
    }
    // Set startDate
    await set(ref(db, `${basePath}/startDate`), new Date().getTime());
    // Set giver and add to participants
    if (uid) {
        await set(ref(db, `${basePath}/giver`), uid);
        // Try to use a readable name for the host
        const profile = store.getters.userProfile || {};
        const displayName = profile.name || profile.displayName || profile.firstName || '';
        const email = profile.email || '';
        const name = displayName || email || 'Host';
        await set(ref(db, `${basePath}/participants/${uid}`), { name });
    }
    return workshopId;
}

//function to load customers
export async function loadCustomers(){
    const db = getDatabase();
    const customersRef = ref(db, 'customers');
    const snapshot = await get(customersRef);
    const customers = snapshot.val() || {};

    // For each customer, load their workshops and attach them to the correct projects
    for (const [customerId, customer] of Object.entries(customers)) {
        if (customer.departments) {
            // Initialize empty workshops object for each project
            for (const department of Object.values(customer.departments)) {
                if (department.projects) {
                    for (const project of Object.values(department.projects)) {
                        if (!project.workshops) {
                            project.workshops = {};
                        }
                    }
                }
            }
        }

        // Load all workshops for this customer
        const workshopsRef = ref(db, `customers/${customerId}/workshop`);
        const workshopsSnapshot = await get(workshopsRef);
        const workshops = workshopsSnapshot.val() || {};

        // Attach each workshop to its corresponding project
        for (const [workshopId, workshop] of Object.entries(workshops)) {
            if (workshop.departmentId && workshop.projectId) {
                const project = customer.departments?.[workshop.departmentId]?.projects?.[workshop.projectId];
                
                if (project) {
                    // Add workshop to project's workshops if not already there
                    if (!project.workshops) {
                        project.workshops = {};
                    }
                    project.workshops[workshopId] = {
                        id: workshopId,
                        name: workshop.name,
                        companyName: workshop.companyName
                    };
                }
            }
        }
    }

    return customers;
}

export async function createCustomer(name){
    const customerId = Math.random().toString(16).slice(2);
    const db = getDatabase();
    // Ref auf den Workshop
    const customerRef = ref(db, `customers/${customerId}`);

    await set(customerRef, {
        id: customerId,
        name: name,
        workshops: [],
    });

    return customerId;
}

export async function deleteCustomer(customerId) {
    const db = getDatabase();
    const customerRef = ref(db, `customers/${customerId}`);
    await remove(customerRef);
}

export async function startVoting(workshopId, segmentId, durationInMIn, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting`);
    }
// Neues Element unter workshop/workshopId hinzufügen
    await set(activeVotingRef, {
        segmentId: segmentId,
        startTime: new Date().getTime(),
        endTime: new Date().getTime()+(durationInMIn*60*1000),
        votes: [],
    });
}

export async function setNewVotingEndTime(workshopId, newEndTime, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting/endTime`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting/endTime`);
    }
// Neues Element unter workshop/workshopId hinzufügen
    await set(activeVotingRef, newEndTime);
}

export async function saveVoting(customerId, workshopId, segmentId, votes, voteWhereWantToBe, voteWhereWeAre, departmentId = null, projectId = null) {
    const db = getDatabase();
    console.log('Saving voting data:', {
        customerId,
        workshopId,
        segmentId,
        votes,
        voteWhereWantToBe,
        voteWhereWeAre
    });

    try {
        // Save to results
        let resultsRef;
        if (departmentId && projectId) {
            resultsRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/results/${segmentId}`);
        } else {
            resultsRef = ref(db, `customers/${customerId}/workshop/${workshopId}/results/${segmentId}`);
        }
        await set(resultsRef, {
            id: segmentId,
            votes: votes,
            voteWhereWantToBe: voteWhereWantToBe,
            voteWhereWeAre: voteWhereWeAre
        });

        // Delete active voting
        let activeVotingRef;
        if (departmentId && projectId) {
            activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting`);
        } else {
            activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting`);
        }
        await set(activeVotingRef, null);

        // Verify the save
        const snapshot = await get(resultsRef);
        console.log('Verified saved voting data:', snapshot.val());
    } catch (error) {
        console.error('Error saving voting:', error);
        throw error;
    }
}

export async function deleteVoting(workshopId, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting`);
    }
// Neues Element unter workshop/workshopId hinzufügen
    await set(activeVotingRef, {});
}

export async function endVoting(workshopId, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting/ended`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting/ended`);
    }
// Neues Element unter workshop/workshopId hinzufügen
    await set(activeVotingRef, true);
}

export async function voting(workshopId, userId, userName, voteWhereWantToBe, voteWhereWeAre, duration, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting/votes`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting/votes`);
    }
// Neues Element unter workshop/workshopId hinzufügen
    await push(activeVotingRef, {
        userId: userId,
        userName: userName,
        duration: duration,
        voteWhereWantToBe: voteWhereWantToBe,
        voteWhereWeAre: voteWhereWeAre,
    });
}

export async function saveSelectedTopics(workshopId, selectedTopics, departmentId = null, projectId = null) {
    if (!workshopId) {
        console.error('saveSelectedTopics: Missing workshopId');
        return;
    }

    try {
        console.log('Saving selected topics:', { workshopId, selectedTopics });
        const db = getDatabase();
        const customerId = store.getters.getCustomerId;
        let topicsRef;
        if (departmentId && projectId) {
            topicsRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/selectedTopics`);
        } else {
            topicsRef = ref(db, `customers/${customerId}/workshop/${workshopId}/selectedTopics`);
        }
        await set(topicsRef, selectedTopics);
        console.log('Selected topics saved successfully');
    } catch (error) {
        console.error('Error saving selected topics:', error);
        throw error;
    }
}

export async function saveWorkPlan(workshopId, workplan, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();

    // Sanitize the workplan data
    const sanitizedWorkplan = workplan.map(item => {
        // Remove undefined values and ensure proper data structure
        const sanitizedItem = {
            id: item.id,
            priority: item.priority || 'Medium',
            effortEstimation: item.effortEstimation || 'M',
            measures: Array.isArray(item.measures) ? 
                item.measures.filter(measure => measure && typeof measure === 'object' && measure.summary) : 
                []
        };
        return sanitizedItem;
    });

    // Ref auf den Workshop
    let workplanRef;
    if (departmentId && projectId) {
        workplanRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/workPlan`);
    } else {
        workplanRef = ref(db, `customers/${customerId}/workshop/${workshopId}/workPlan`);
    }
    // Neues Element unter workshop/workshopId hinzufügen
    await set(workplanRef, sanitizedWorkplan);
}

export async function saveNotice(workshopId, id, notice, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/notice/${id}`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/notice/${id}`);
    }
// Neues Element unter workshop/workshopId hinzufügen
    await set(activeVotingRef, notice);
}

export async function onNoticeChange(workshopId, callbackFunction, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/notice`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/notice`);
    }

    onValue(activeVotingRef, (snapshot) => {
        callbackFunction(snapshot.val());
    });
}

export async function onActiveVotingChange(workshopId, callbackFunction, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting`);
    }

    onValue(activeVotingRef, (snapshot) => {
        callbackFunction(snapshot.val());
    });
}

export async function onActiveVotingVotesChange(workshopId, callbackFunction, departmentId = null, projectId = null) {
    const customerId = store.getters.getCustomerId;
    const db = getDatabase();
    let activeVotingRef;
    if (departmentId && projectId) {
        activeVotingRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/activeVoting/votes`);
    } else {
        activeVotingRef = ref(db, `customers/${customerId}/workshop/${workshopId}/activeVoting/votes`);
    }

    onValue(activeVotingRef, (snapshot) => {
        if(snapshot.val() == null){
            callbackFunction([]);
        } else {
            callbackFunction(Object.values(snapshot.val()));
        }
    });
}

// Tasks laden
export async function loadTasks(customerId, workshopId, departmentId = null, projectId = null) {
    const db = getDatabase();
    let tasksRef;
    if (departmentId && projectId) {
        tasksRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/tasks`);
    } else {
        tasksRef = ref(db, `customers/${customerId}/workshop/${workshopId}/tasks`);
    }
    const snapshot = await get(tasksRef);
    const tasks = snapshot.val();
    return tasks
        ? Object.values(tasks).map((task) => ({
            ...task,
            dueDate: new Date(task.dueDate),
            dirtyFlags: {}, // Initialisiere dirtyFlags für jedes Feld
        }))
        : [];
}

// Task speichern
export async function saveTask(customerId, workshopId, task, departmentId = null, projectId = null) {
    task.dueDate = task.dueDate.getTime();
    const db = getDatabase();
    let taskRef;
    if (departmentId && projectId) {
        taskRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/tasks/${task.id}`);
    } else {
        taskRef = ref(db, `customers/${customerId}/workshop/${workshopId}/tasks/${task.id}`);
    }
    await set(taskRef, task);
}

// Task hinzufügen
export async function addTask(customerId, workshopId, task, departmentId = null, projectId = null) {
    const db = getDatabase();
    let taskRef;
    if (departmentId && projectId) {
        taskRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/tasks`);
    } else {
        taskRef = ref(db, `customers/${customerId}/workshop/${workshopId}/tasks`);
    }

    // Use push() to generate a unique ID and create a new child reference
    const newTaskRef = push(taskRef);
    const taskId = newTaskRef.key;

    // Remove undefined values and add default properties
    const sanitizedTask = Object.fromEntries(
        // eslint-disable-next-line no-unused-vars
        Object.entries(task).filter(([_, value]) => value !== undefined)
    );

    sanitizedTask.id = taskId; // Assign the generated ID to the task
    sanitizedTask.dueDate = sanitizedTask.dueDate || new Date().toISOString(); // Default dueDate

    await set(newTaskRef, sanitizedTask);
    return taskId; // Return the generated ID
}

// Task löschen
export async function deleteTask(customerId, workshopId, task, departmentId = null, projectId = null) {
    const db = getDatabase();
    let basePath;
    if (departmentId && projectId) {
        basePath = `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/tasks`;
    } else {
        basePath = `customers/${customerId}/workshop/${workshopId}/tasks`;
    }
    const tasksRef = ref(db, basePath);
    const snapshot = await get(tasksRef);
    if (!snapshot.exists()) return false;

    const tasks = snapshot.val();
    // Prefer delete by id when present, else fallback to summary+qualityAspect match
    let taskKey = null;
    if (task && task.id) {
        taskKey = Object.keys(tasks).find(key => tasks[key].id === task.id);
    }
    if (!taskKey) {
        taskKey = Object.keys(tasks).find(key =>
            tasks[key].summary === task.summary &&
            tasks[key].qualityAspect === task.qualityAspect
        );
    }
    if (!taskKey) return false;

    const taskRef = ref(db, `${basePath}/${taskKey}`);
    await remove(taskRef);
    return true;
}

export async function checkConflickts(task, tracker){
    return await checkConflict(task.trackerKey, task, tracker);
}

export async function updateIssue(customerId, workshopId, task, tracker, departmentId = null, projectId = null) {
    try {
        if (!tracker) {
            throw new Error("Tracker is not configured");
        }

        // Check if the task already exists in the tracker
        if (task.trackerKey) {
            console.log(`Updating issue ${task.trackerKey} in the tracker`);

            // Update existing issue in the tracker
            const updatedKey = await tracker.updateIssue(task.trackerKey, task);

            let duedate = task.dueDate;
            if(!(duedate instanceof Date)){
                duedate = new Date(duedate);
            }

            const originalTask = {
                summary: task.summary,
                assignee: task.assignee,
                parentIssue: task.parentIssue,
                qualityAspect: task.qualityAspect,
                dueDate: duedate.getTime(),
                originalEffort: task.originalEffort,
                description: task.description,
            };

            console.log(`Issue ${updatedKey} updated successfully`);
            task.originalValues = {
                ...originalTask
            }
            // Reset dirty flags after a successful update
            for (const key in task.dirty) {
                task.dirty[key] = false;
            }

            // Save the updated task with the tracker key to Firebase
            await saveTask(customerId, workshopId, task, departmentId, projectId);
        } else {
            console.log("Creating new issue in the tracker");
            let duedate = task.dueDate;
            if(!(duedate instanceof Date)){
                duedate = new Date(duedate);
            }
            const newtask = {
                summary: task.summary,
                assignee: task.assignee,
                parentIssue: task.parentIssue,
                qualityAspect: task.qualityAspect,
                dueDate: duedate.getTime(),
                originalEffort: task.originalEffort,
                description: task.description,
            };
            // Create a new issue in the tracker
            const newKey = await tracker.createIssue(newtask);
            task.originalValues = {
                ...newtask
            }

            // Reset dirty flags after a successful update
            for (const key in task.dirty) {
                task.dirty[key] = false;
            }

            console.log(`New issue created with key ${newKey}`);

            // Update the task with the tracker key
            task.trackerKey = newKey;

            // Save the updated task with the tracker key to Firebase
            await saveTask(customerId, workshopId, task, departmentId, projectId);
        }
    } catch (error) {
        console.error("Error updating issue", error);
        throw error;
    }
}

/**
 * Check for conflicts between local and Jira data
 * @param {string} issueKey - Jira issue key
 * @param {Object} localIssue - Locally stored issue with original values and dirty flags
 * @returns {Object} - Object with conflict details
 */
export async function checkConflict(issueKey, localIssue, tracker) {
    // Fetch the latest issue data from Jira
    const trackerIssue = await tracker.getIssue(issueKey);

    const conflicts = {};

    // Map local fields to Jira fields for comparison
    const fieldMapping = {
        summary: "summary",
        dueDate: "duedate",
        assignee: "assignee",
        description: "description",
        parentIssue: "parent",
        labels: "labels",
        qualityAspect: "qualityAspect",
    };

    for (const [localField, jiraField] of Object.entries(fieldMapping)) {
        const localOriginal = localIssue.originalValues[jiraField];
        const trackerFieldCurrent = trackerIssue[jiraField];
        const localDirty = localIssue.dirty[localField];

        if (localDirty && localOriginal !== undefined) {
            // Check if Jira has changed since the last fetch
            if (localOriginal !== trackerFieldCurrent) {
                conflicts[localField] = {
                    localOriginal,
                    trackerFieldCurrent,
                    localDirtyValue: localIssue[localField],
                };
            }
        }
    }

    return conflicts;
}

export async function deleteWorkshop(customerId, departmentId, projectId, workshopId) {
    const db = getDatabase();
    
    // Remove workshop from workshops collection
    const workshopRef = ref(db, `customers/${customerId}/workshop/${workshopId}`);
    await remove(workshopRef);
    
    // Remove workshop reference from project
    const projectWorkshopRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}`);
    await remove(projectWorkshopRef);
}

export async function saveRiskAssessment(customerId, workshopId, riskData, departmentId = null, projectId = null) {
    if (!customerId || !workshopId || !riskData) {
        console.error('saveRiskAssessment: Missing required parameters');
        return;
    }

    try {
        const db = getDatabase();
        let riskRef;
        if (departmentId && projectId) {
            riskRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/riskAssessment`);
        } else {
            riskRef = ref(db, `customers/${customerId}/workshop/${workshopId}/riskAssessment`);
        }
        await set(riskRef, riskData);
        console.log('Risk assessment saved successfully');
    } catch (error) {
        console.error('Error saving risk assessment:', error);
        throw error;
    }
}

export async function updateQualityMetrics(customerId, workshopId, metrics, departmentId = null, projectId = null) {
    if (!customerId || !workshopId || !metrics) {
        console.error('updateQualityMetrics: Missing required parameters');
        return;
    }

    try {
        const db = getDatabase();
        let metricsRef;
        if (departmentId && projectId) {
            metricsRef = ref(db, `customers/${customerId}/departments/${departmentId}/projects/${projectId}/workshops/${workshopId}/qualityMetrics`);
        } else {
            metricsRef = ref(db, `customers/${customerId}/workshop/${workshopId}/qualityMetrics`);
        }
        await set(metricsRef, metrics);
        console.log('Quality metrics updated successfully');
    } catch (error) {
        console.error('Error updating quality metrics:', error);
        throw error;
    }
}
